import { useMemo } from 'react'

import { useTheme } from 'styled-components'

import {
  useGetPerformanceDetailsByIdQuery,
  useGetPerformanceTotalsByIdQuery,
} from 'src/Redux/adsCampaignApi'
import dayjs from 'src/Helpers/dayjs'
import {
  useGetMetaAdsCampaignDetailsQuery,
  MetaAdsStrategy,
} from 'src/Redux/metaAdsDetailsApi'

export type ChartData = {
  labels: (string | Date)[]
  datasets: {
    label: string
    data: number[]
    borderColor: string
    backgroundColor: string
    tension: number
    yAxisID: string
  }[]
}
interface UseChart {
  chartData?: ChartData | null
}

const formatDateToISO = (date?: Date | string | null): string =>
  date ? dayjs(date).toISOString() : dayjs().toISOString()

const DASH_VALUE = 3

export const useChart = ({
  campaignId,
  startDate,
  endDate,
}: {
  campaignId: string
  startDate?: string
  endDate?: string
}): UseChart => {
  const theme = useTheme()
  const { data } = useGetMetaAdsCampaignDetailsQuery(campaignId, {
    skip: !campaignId || Number(campaignId) === 0,
  })

  const { data: performanceTotalsById } = useGetPerformanceTotalsByIdQuery(
    campaignId || '',
    {
      skip: !campaignId,
    },
  )

  const dateTo = formatDateToISO(endDate)
  const dateFrom = formatDateToISO(startDate)

  const { data: campaignPerformanceData } = useGetPerformanceDetailsByIdQuery(
    {
      adCampaignId: campaignId.toString(),
      dateFrom,
      dateTo,
    },
    {
      skip: !startDate || !endDate,
    },
  )

  const chartData = useMemo(() => {
    if (!campaignPerformanceData || campaignPerformanceData.length === 0) {
      return null
    }

    const sortedData = [...campaignPerformanceData].sort(
      (a, b) =>
        new Date(a.date ?? '').getTime() - new Date(b.date ?? '').getTime(),
    )

    const processedData = sortedData.map((item) => {
      const daysSinceCampaignStart =
        dayjs(item.date)
          .utc()
          .startOf('day')
          .diff(dayjs(data?.startDate).utc().startOf('day'), 'days') + 1

      const budgetSpent = (data?.dailyBudget || 0) * daysSinceCampaignStart

      const cumulativeCPF =
        item.followersAddedSinceStart > 0
          ? Number((budgetSpent / item.followersAddedSinceStart).toFixed(2))
          : 0

      return {
        ...item,
        followers: item.followersAddedSinceStart,
        costPerFollower: cumulativeCPF,
      }
    })

    const labels = processedData.map((item) => item.date ?? '')

    const datasets =
      data?.strategy === MetaAdsStrategy.TRACK
        ? [
            {
              label: 'Ad clicks',
              data: processedData.map((item) => item.clicks),
              borderColor: theme.color.brandGreen,
              backgroundColor: theme.color.white,
              tension: 0.4,
              yAxisID: 'y',
              borderWidth: 1,
              borderDash: [DASH_VALUE, DASH_VALUE],
            },
            ...(performanceTotalsById?.totalConversions !== null
              ? [
                  {
                    label: 'Conversions',
                    data: processedData.map((item) => item.conversions),
                    borderColor: theme.color.brandGreen,
                    backgroundColor: theme.color.white,
                    tension: 0.4,
                    yAxisID: 'y',
                    borderWidth: 2,
                  },
                ]
              : []),
          ]
        : [
            {
              label: 'Ad clicks',
              data: processedData.map((item) => item.clicks),
              borderColor: theme.color.brandGreen,
              backgroundColor: theme.color.white,
              tension: 0.4,
              yAxisID: 'y',
              borderWidth: 2,
              borderDash: [DASH_VALUE, DASH_VALUE],
            },
            {
              label: 'Playlist followers',
              data: processedData.map((item) => item.followers),
              borderColor: theme.color.brandGreen,
              backgroundColor: theme.color.white,
              tension: 0.4,
              yAxisID: 'y',
              borderWidth: 2,
            },
            {
              label: 'Cost per follower',
              data: processedData.map((item) => item.costPerFollower),
              borderColor: theme.color.mediumOrange,
              backgroundColor: theme.color.white,
              tension: 0.4,
              yAxisID: 'y1',
              borderWidth: 2,
            },
          ]

    return {
      labels,
      datasets,
    }
  }, [
    campaignPerformanceData,
    data?.dailyBudget,
    data?.startDate,
    data?.strategy,
    performanceTotalsById?.totalConversions,
    theme.color.brandGreen,
    theme.color.white,
    theme.color.mediumOrange,
  ])

  return { chartData }
}
